import { Link } from "react-router-dom";
import { ArrowRight, Leaf, FileText } from "lucide-react";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { ScrollReveal } from "@/components/ScrollReveal";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/me2m4ca0_4bf83c1de210c7ab791f654b4214a201.jpg";
const CAPABILITY_STATEMENT_URL = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/wvqxqo67_PKC%20Property%20Services%20-%20Capability%20Statement%202026.pdf";
// HD 4K hero image - PKC team at work
const HERO_BG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/5yac7zeq_75685aff-5ee8-429b-9131-0df5d83d2f47.png";

const VALUES = [
  { title: "Precision", description: "Every project is delivered with attention to detail, ensuring consistent, high-standard results." },
  { title: "Safety", description: "Full OHS compliance, SWMS documentation, and insured operations across all sites." },
  { title: "Reliability", description: "We operate to your schedule, with the capacity to meet tight construction timelines." },
  { title: "Partnership", description: "We work as an extension of your team, providing clear communication and dependable service throughout." },
];

const PROCESS_STEPS = [
  { step: "01", title: "Site Assessment", desc: "We assess your site to understand scope, timelines and requirements, ensuring a tailored approach from the outset." },
  { step: "02", title: "Custom Quote", desc: "A detailed, transparent proposal outlining scope, inclusions and deliverables. No ambiguity." },
  { step: "03", title: "Mobilisation", desc: "Our team arrives fully equipped and aligned with your site requirements, coordinating with project managers to minimise disruption." },
  { step: "04", title: "Execution", desc: "We deliver cleaning services methodically across all areas, with supervision ensuring consistent quality throughout." },
  { step: "05", title: "Quality Assurance", desc: "A comprehensive inspection is completed prior to sign-off, with any items addressed immediately." },
  { step: "06", title: "Handover Ready", desc: "Your site is delivered to a high standard, ready for immediate use or occupancy." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero - Job site photo with overlay */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-28 relative overflow-hidden min-h-[60vh] md:min-h-[50vh]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="" 
            className="w-full h-full object-cover object-[center_50%] md:object-[center_40%]"
          />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/50" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.3em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              About Us
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Built on Quality
              <span className="text-[#B8D86B]">.</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Since 2022, PKC Property Services has delivered high-standard commercial cleaning across Melbourne's construction, commercial and industrial sectors.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#B8D86B] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Our Story</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-neutral-900 tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Built From the Ground Up.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="text-neutral-600 leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PKC Property Services was established in 2022 with a clear objective — deliver reliable, high-quality cleaning without compromise. Too often, builders were left under pressure before handover due to inconsistent contractors who failed to meet expectations. We built PKC to be different.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              From the outset, we've worked closely with builders, developers and property managers across Melbourne, providing a dependable service built on accountability and attention to detail. We show up when scheduled, deliver to a consistent standard, and ensure every site is completed properly.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Today, our team of over 45 professionals services projects ranging from office fitouts to large-scale residential developments and industrial facilities. With more than 250 projects completed, PKC has built long-term partnerships with leading construction and property groups across Melbourne.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="about-cta-btn"
                className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-8 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Work With Us
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="/api/capability-statement"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="capability-statement-btn"
                className="inline-flex items-center gap-2 bg-white text-neutral-900 px-8 py-4 text-sm font-semibold border border-neutral-300 hover:border-neutral-400 hover:bg-neutral-50 transition-colors duration-300"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                <FileText className="w-4 h-4" />
                Capability Statement
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Environmental Commitment Section */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <ScrollReveal>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#B8D86B]/20 rounded-full flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-[#B8D86B]" />
                  </div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#B8D86B]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Our Commitment
                  </p>
                </div>
                <h2 className="text-4xl md:text-5xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Sustainability & Environment.
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <p className="text-neutral-600 leading-relaxed mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  At PKC Property Services, we recognise our responsibility to minimise environmental impact and operate sustainably across every project.
                </p>
                <p className="text-neutral-600 leading-relaxed mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Sustainability is embedded in how we work — from the products we use to the processes we follow on-site. We prioritise environmentally responsible methods that reduce chemical use, conserve water, and support safe waste management practices.
                </p>
                <p className="text-neutral-600 leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  We partner with suppliers who share our standards, ensuring our entire supply chain aligns with our environmental commitments. For organisations with environmental requirements, PKC delivers cleaning solutions that meet recognised compliance standards.
                </p>
              </ScrollReveal>
            </div>
            <ScrollReveal delay={0.1}>
              <div className="bg-[#B8D86B]/10 p-8 md:p-12 border-l-4 border-[#B8D86B]">
                <h3 className="text-xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Our Environmental Practices
                </h3>
                <ul className="space-y-3">
                  {[
                    "Biodegradable and non-toxic cleaning products",
                    "Water-efficient cleaning methods",
                    "Responsible waste segregation and recycling",
                    "Energy-efficient equipment and operations",
                    "Compliance with EPA guidelines",
                    "Ongoing staff training in sustainable practices",
                    "Continuous reduction of environmental impact"
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-[#B8D86B] rounded-full mt-2 shrink-0"></span>
                      <span className="text-neutral-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16">
            <div className="text-left">
              <AnimatedCounter end={250} suffix="+" label="Projects Completed" darkMode={false} />
            </div>
            <div className="text-left">
              <AnimatedCounter end={4} label="Years Experience" darkMode={false} />
            </div>
            <div className="text-left">
              <AnimatedCounter end={120} suffix="+" label="Clients Served" darkMode={false} />
            </div>
            <div className="text-left">
              <AnimatedCounter end={45} suffix="+" label="Team Members" darkMode={false} />
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="max-w-2xl mb-16">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#B8D86B] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Our Process</p>
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                A Proven Approach.
              </h2>
              <p className="text-neutral-500 mt-4 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                From initial engagement through to final handover, our process ensures consistent, high-quality outcomes.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {PROCESS_STEPS.map((item, idx) => (
              <ScrollReveal key={item.step} delay={idx * 0.1}>
                <div className="bg-white p-8 h-full border-l-4 border-[#B8D86B] hover:shadow-md transition-all duration-300" data-testid={`process-step-${idx}`}>
                  <span className="text-5xl font-bold text-[#B8D86B]/20">{item.step}</span>
                  <h3 className="text-xl font-semibold text-neutral-900 mt-2 mb-3 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-neutral-500 text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="max-w-2xl mb-16">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#B8D86B] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Our Values</p>
              <h2 className="text-4xl md:text-5xl font-semibold text-neutral-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                What Drives Us
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((value, idx) => (
              <ScrollReveal key={value.title} delay={idx * 0.1}>
                <div className="bg-neutral-50 p-8 h-full hover:shadow-md transition-shadow duration-300" data-testid={`value-card-${idx}`}>
                  <h3 className="text-lg font-bold text-neutral-900 mb-3 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{value.title}</h3>
                  <div className="w-8 h-0.5 bg-[#B8D86B] mb-4" />
                  <p className="text-neutral-500 text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{value.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ready to partner with us?
            </h2>
            <p className="text-neutral-600 text-lg mb-10 max-w-lg mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Discover how PKC delivers consistent, high-standard cleaning across commercial environments.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-10 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
